import { useCallback, useEffect, useRef, useState } from "react";
import {
  Calendar,
  Camera,
  ChevronDown,
  ChevronRight,
  Clock,
  Edit,
  ExternalLink,
  Eye,
  EyeOff,
  History,
  Mic,
  Paperclip,
  Printer,
  RotateCcw,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/v5/components/ui/button";
import { Separator } from "@/v5/components/ui/separator";
import { Spinner } from "@/v5/components/ui/spinner";
import type { NoteHistoryEntry, PatientNoteDetail } from "@/v5/services/notes.service";
import * as notesSvc from "@/v5/services/notes.service";
import { CameraCaptureModal } from "@/components/medical/CameraCaptureModal";
import { PrintOptionsDialog } from "@/v5/components/PrintOptionsDialog";
import { LabPdfPreviewPanel } from "@/pages/patients/labs/LabPdfPreviewPanel";
import { isPreviewableFilename } from "@/lib/previewableFiles";
import { toast } from "@/store/toastStore";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { pacificToWallClock, getTimezoneAbbreviation } from "@/lib/facilityTimezone";
import { formatNoteApptLink } from "@/v5/lib/noteAppointmentLabel";
import { useAuth } from "@/hooks/useAuth";
import { TaskModal } from "@/pages/TasksPage";
import type { PatientSummary } from "@/types/patient.types";

interface NoteDetailPanelProps {
  patientId: string;
  noteId: number;
  /** Prefills the patient on the Add Task modal. Optional: without it the task
   *  is still created and still linked to the note, the picker just starts
   *  empty and the user selects the patient themselves. */
  patientSummary?: PatientSummary | null;
  onExpand: () => void;
  onClose: () => void;
  onEdit: (noteId: number) => void;
  /** Called after a successful delete/restore so the parent list can refresh. */
  onMutated?: () => void;
  /**
   * Bumped by the parent when the note is mutated elsewhere (e.g. from the
   * expanded NoteViewModal that renders on top of this panel). A change forces
   * a refetch so this panel never shows stale state after the modal closes.
   */
  refreshTrigger?: number;
}

function fmt(d: string | null, userTz?: string): string {
  if (!d) return "\u2014";
  try {
    const raw = d.replace(" ", "T");
    const pacDate = raw.slice(0, 10);
    const pacTime = raw.slice(11, 16) || "00:00";
    const tz = userTz || "America/Los_Angeles";
    const wall = pacificToWallClock(pacDate, pacTime, tz);
    const dt = new Date(`${wall.date}T${wall.time}:00Z`);
    if (Number.isNaN(dt.getTime())) return d;
    const formatted = dt.toLocaleString("en-US", {
      // `dt` is the resolved wall clock anchored to UTC, so formatting in UTC
      // prints those components; formatting in `tz` would shift them again.
      timeZone: "UTC",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    return `${formatted} ${getTimezoneAbbreviation(tz, dt)}`;
  } catch {
    return d;
  }
}

export function NoteDetailPanel({
  patientId,
  noteId,
  patientSummary,
  onExpand,
  onClose,
  onEdit,
  onMutated,
  refreshTrigger,
}: NoteDetailPanelProps) {
  const userTz = useUserTimezone();
  const { user: currentUser } = useAuth();
  const [note, setNote] = useState<PatientNoteDetail | null>(null);
  const [history, setHistory] = useState<NoteHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState<Set<number>>(new Set());
  const [cameraOpen, setCameraOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  // Attachment being previewed, rendered as an overlay over this panel's body.
  const [previewAttachment, setPreviewAttachment] = useState<
    { attachmentId: number; filename: string } | null
  >(null);

  const fetchNote = useCallback(async () => {
    setLoading(true);
    try {
      const [noteData, histData] = await Promise.all([
        notesSvc.getNote(patientId, noteId),
        notesSvc.getNoteHistory(patientId, noteId),
      ]);
      setNote(noteData);
      setHistory(histData);
    } catch {
      toast.error("Failed to load note");
    } finally {
      setLoading(false);
    }
  }, [patientId, noteId]);

  useEffect(() => {
    void fetchNote();
  }, [fetchNote]);

  // Selecting a different note must drop any open preview, otherwise the PDF
  // from the previous note keeps covering this panel.
  useEffect(() => {
    setPreviewAttachment(null);
  }, [patientId, noteId]);

  // Refetch when the parent signals an external mutation (mirrors the
  // expandTrigger pattern in DetailRightPanel). The ref skips the initial
  // value so this doesn't double-fetch on mount alongside the effect above.
  const prevRefreshTrigger = useRef(refreshTrigger);
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger !== prevRefreshTrigger.current) {
      prevRefreshTrigger.current = refreshTrigger;
      void fetchNote();
    }
  }, [refreshTrigger, fetchNote]);

  async function handleDelete() {
    if (!window.confirm("Delete this note?")) return;
    try {
      await notesSvc.deleteNote(patientId, noteId);
      toast.success("Note deleted");
      onMutated?.();
      onClose();
    } catch {
      toast.error("Failed to delete note");
    }
  }

  async function handleRestore() {
    if (!window.confirm("Restore this note?")) return;
    try {
      await notesSvc.restoreNote(patientId, noteId);
      toast.success("Note restored");
      onMutated?.();
      void fetchNote();
    } catch {
      toast.error("Failed to restore note");
    }
  }

  async function handleTogglePortal() {
    try {
      const result = await notesSvc.togglePortalVisibility(patientId, noteId);
      setNote((prev) =>
        prev && { ...prev, visibleToPatient: result.visible, portalId: result.portalId },
      );
      toast.success(
        result.visible ? "Note now visible to patient" : "Note hidden from patient",
      );
      onMutated?.();
    } catch {
      toast.error("Failed to toggle visibility");
    }
  }

  function toggleHistoryItem(id: number) {
    setExpandedHistory((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const apptChipLabel = formatNoteApptLink(note?.appointments?.[0], userTz);

  return (
    <div className="relative flex h-full min-h-0 w-full min-w-0 flex-col bg-white">
      {/* Attachment preview takes over the panel. Same viewer the Labs >
          Documents tab uses for these very rows, so both surfaces behave
          identically; an overlay (rather than window.open) keeps popup
          blockers out of the picture. */}
      {previewAttachment && (
        <div className="absolute inset-0 z-10 bg-white">
          <LabPdfPreviewPanel
            patientId={Number(patientId)}
            target={{
              kind: "attachment",
              patientId: Number(patientId),
              attachmentId: previewAttachment.attachmentId,
              title: previewAttachment.filename,
            }}
            onClose={() => setPreviewAttachment(null)}
            onNewTab={() => setPreviewAttachment(null)}
          />
        </div>
      )}

      {/* Top bar — mirrors PatientsPage right-panel header */}
      <div className="flex items-center justify-between gap-1 border-b border-slate-200 px-3 py-1.5">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          title="Close panel"
          aria-label="Close panel"
        >
          <X className="h-4 w-4" />
        </button>
        <Button variant="ghost" size="sm" className="shrink-0" onClick={onExpand}>
          <ExternalLink className="mr-1 h-3.5 w-3.5" />
          Open
        </Button>
      </div>

      {/* Body */}
      {loading || !note ? (
        <div className="flex flex-1 items-center justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {/* Title + meta */}
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Note #{note.noteId}
            </div>
            <h3 className="text-base font-semibold text-slate-900 leading-snug">
              {note.title}
            </h3>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {fmt(note.noteDate, userTz)}
              </span>
              {note.typeName && (
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                  {note.typeName}
                </span>
              )}
              {/* Appointment this note is linked to. Matches the type pill's styling
                  rather than pulling in Badge for one chip. */}
              {apptChipLabel && (
                <span
                  className="inline-flex max-w-[240px] items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                  title={apptChipLabel}
                >
                  <Calendar className="h-3 w-3 shrink-0" />
                  <span className="truncate">{apptChipLabel}</span>
                </span>
              )}
              {note.authorName && (
                <span className="text-slate-500">by {note.authorName}</span>
              )}
            </div>
          </div>

          {/* Action toolbar */}
          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTogglePortal}
              className="gap-1"
              title={
                note.visibleToPatient
                  ? "Visible to patient — click to hide"
                  : "Hidden from patient — click to show"
              }
            >
              {note.visibleToPatient ? (
                <>
                  <Eye className="h-3.5 w-3.5 text-green-600" /> Visible
                </>
              ) : (
                <>
                  <EyeOff className="h-3.5 w-3.5" /> Hidden
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCameraOpen(true)}
              className="gap-1"
              title="Capture Photo"
            >
              <Camera className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" className="gap-1" title="Add Recording">
              <Mic className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTaskOpen(true)}
              className="gap-1"
              title="Add Task"
            >
              <Tag className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPrintOpen(true)}
              className="gap-1"
              title="Print"
            >
              <Printer className="h-3.5 w-3.5" />
            </Button>
            {note.active === 1 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="gap-1 text-destructive"
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRestore}
                className="gap-1 text-green-600"
                title="Restore"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
            <div className="ml-auto" />
            {note.active === 1 && currentUser?.id === note.authorId && (
              <Button size="sm" onClick={() => onEdit(noteId)} className="gap-1">
                <Edit className="h-3.5 w-3.5" /> Edit
              </Button>
            )}
          </div>

          {/* Only rendered when we actually know who deleted it -- notes deleted
              before deletions were recorded have no name, and a half-built
              "Deleted by" line would be worse than staying quiet. */}
          {note.active !== 1 && note.deletedBy && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              Deleted by {note.deletedBy}
              {note.deletedAt ? ` · ${fmt(note.deletedAt, userTz)}` : ''}
            </div>
          )}

          {/* Note content */}
          <div className="space-y-1">
            <div className="text-xs font-medium text-slate-500">Note</div>
            <div
              className="prose prose-sm max-w-none rounded-md border border-slate-200 bg-slate-50/50 p-3 text-slate-800"
              dangerouslySetInnerHTML={{ __html: note.content }}
            />
          </div>

          {/* Attachments */}
          <div className="space-y-1">
            <div className="text-xs font-medium text-slate-500">
              Attachments ({note.attachments.length})
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3">
              {note.attachments.length === 0 ? (
                <span className="text-xs text-slate-400">No attachments</span>
              ) : (
                <div className="space-y-1">
                  {note.attachments.map((a) => (
                    <button
                      key={a.attachmentId}
                      type="button"
                      onClick={() => {
                        // PDFs/images preview in place; anything the browser
                        // can't render (.docx, .label) downloads instead.
                        if (isPreviewableFilename(a.filename)) {
                          setPreviewAttachment({
                            attachmentId: a.attachmentId,
                            filename: a.filename,
                          });
                          return;
                        }
                        void notesSvc
                          .downloadAttachment(patientId, a.attachmentId, a.filename)
                          .catch((err) =>
                            toast.error(
                              err instanceof Error
                                ? err.message
                                : "Failed to download attachment",
                            ),
                          );
                      }}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                    >
                      <Paperclip className="h-3 w-3" />
                      {a.filename}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* History */}
          <div>
            <button
              type="button"
              onClick={() => setHistoryOpen((o) => !o)}
              className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-primary"
            >
              <History className="h-4 w-4" />
              History ({history.length})
              {historyOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            {historyOpen && (
              <div className="mt-3 space-y-2 pl-2">
                {history.length === 0 ? (
                  <p className="text-xs text-slate-400">No history available</p>
                ) : (
                  history.map((h) => (
                    <div
                      key={h.historyId}
                      className="overflow-hidden rounded-md border border-slate-200"
                    >
                      <button
                        type="button"
                        onClick={() => toggleHistoryItem(h.historyId)}
                        className="flex w-full items-center justify-between bg-slate-50 px-3 py-2 text-xs hover:bg-slate-100"
                      >
                        <span className="truncate text-left">
                          <strong>{h.authorName || "System"}</strong> {"\u2014"}{" "}
                          {fmt(h.dateCreated, userTz)}
                        </span>
                        {expandedHistory.has(h.historyId) ? (
                          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                        )}
                      </button>
                      {expandedHistory.has(h.historyId) && (
                        <div className="space-y-2 p-3 text-xs">
                          <div>
                            <strong>Subject:</strong> {h.title}
                          </div>
                          <div
                            className="prose prose-xs max-w-none"
                            dangerouslySetInnerHTML={{ __html: h.content }}
                          />
                          {h.attachmentsAdded.length > 0 && (
                            <div className="text-green-600">
                              <strong>Files added:</strong>{" "}
                              {h.attachmentsAdded.map((a) => a.filename).join(", ")}
                            </div>
                          )}
                          {h.attachmentsRemoved.length > 0 && (
                            <div className="text-destructive">
                              <strong>Files removed:</strong>{" "}
                              {h.attachmentsRemoved.map((a) => a.filename).join(", ")}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Camera capture — v5-native replacement for the legacy
          /patient_photo_capture.php popup. Each photo lands in
          notes_attachments via the same endpoint as the regular file
          picker, then we refetch so the new file appears immediately
          in the Attachments list above. */}
      <CameraCaptureModal
        isOpen={cameraOpen}
        multi
        onCapture={(file) => {
          void notesSvc
            .uploadAttachment(patientId, noteId, file, { mirrorToPhotos: true })
            .then(() => {
              toast.success("Photo attached");
              void fetchNote();
              onMutated?.();
            })
            .catch(() => toast.error("Failed to attach photo"));
        }}
        onClose={() => setCameraOpen(false)}
      />

      {printOpen && (
        <PrintOptionsDialog
          patientId={patientId}
          noteIds={String(noteId)}
          mode="pdf"
          onClose={() => setPrintOpen(false)}
        />
      )}

      {/* Raise a task from this note. initialNoteId stamps plan_tasks.noteID so
          the task carries the link; nothing in the UI reads it back yet. */}
      {taskOpen && (
        <TaskModal
          initialPatient={patientSummary ?? undefined}
          initialNoteId={noteId}
          onClose={() => setTaskOpen(false)}
          onSaved={() => setTaskOpen(false)}
        />
      )}
    </div>
  );
}
